import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { apiRequest, ApiError } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { DateInput } from '../../../../shared/components/DateInput.js';
import { Button, DataTable } from '../../../../shared/components/index.js';

interface LicenseRow {
  readonly rightsLicenseId: string;
  readonly licenseCode: string;
  readonly licenseName: string;
  readonly assetId: string | null;
  readonly licenseType: string;
  readonly copyrightOwner: string | null;
  readonly status: string;
}

const LICENSE_TYPES = ['', 'ROYALTY_FREE', 'RIGHTS_MANAGED', 'EXCLUSIVE', 'PERPETUAL', 'SUBSCRIPTION', 'CREATIVE_COMMONS', 'PROPRIETARY'];
const CREATE_TYPES = ['ROYALTY_FREE', 'RIGHTS_MANAGED', 'EXCLUSIVE', 'PERPETUAL', 'SUBSCRIPTION', 'CREATIVE_COMMONS', 'PROPRIETARY'];
const STATUSES = ['', 'PENDING', 'ACTIVE', 'EXPIRED', 'REVOKED', 'RENEWED'];

/**
 * M36 — Rights Management Screen (§31). Register of asset rights licences
 * (T488) across the 7 licence types and the pending → active → expired/revoked
 * lifecycle, tracking the copyright owner. Expired licences restrict downstream
 * asset usage (BR-02). Filterable by licence type, status and a free-text
 * search. Tenant isolation is enforced server-side (LAW 4).
 */
export function RightsManagementScreen(): ReactNode {
  const { token } = useAuth();
  const { t } = useTranslation();
  const [rows, setRows] = useState<LicenseRow[]>([]);
  const [total, setTotal] = useState(0);
  const [licenseType, setLicenseType] = useState('');
  const [status, setStatus] = useState('');
  const [q, setQ] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [reload, setReload] = useState(0);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [newName, setNewName] = useState('');
  const [newType, setNewType] = useState(CREATE_TYPES[0]);
  const [newAssetId, setNewAssetId] = useState('');
  const [newOwner, setNewOwner] = useState('');
  const [newValidityEnd, setNewValidityEnd] = useState('');
  const [renewId, setRenewId] = useState<string | null>(null);
  const [renewEnd, setRenewEnd] = useState('');

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const params = new URLSearchParams();
    if (licenseType) params.set('licenseType', licenseType);
    if (status) params.set('status', status);
    if (q) params.set('q', q);
    const qs = params.toString();
    apiRequest<{ data: LicenseRow[]; total: number }>(`/v1/dam/rights${qs ? `?${qs}` : ''}`, { token })
      .then((r) => { if (alive) { setRows(r.data); setTotal(r.total); setError(null); } })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token, licenseType, status, q, reload]);

  const on403 = (err: unknown, fallback: string): void => {
    if (err instanceof ApiError && err.status === 403) setNotice(t('m36.common.permRequired', { perm: 'M36_RIGHTS_MANAGE' }));
    else setNotice((err as Error).message ?? fallback);
  };

  const create = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setNotice(null);
    if (!newName.trim()) { setNotice(t('m36.rights.nameReq')); return; }
    setBusy(true);
    try {
      await apiRequest(`/v1/dam/rights`, {
        method: 'POST',
        body: {
          licenseName: newName.trim(),
          licenseType: newType,
          assetId: newAssetId.trim() || undefined,
          copyrightOwner: newOwner.trim() || undefined,
          validityEnd: newValidityEnd.trim() || undefined,
        },
        token,
      });
      setNotice(t('m36.rights.created', { name: newName.trim() }));
      setNewName(''); setNewAssetId(''); setNewOwner(''); setNewValidityEnd('');
      setReload((n) => n + 1);
    } catch (err) {
      on403(err, t('m36.rights.createFailed'));
    } finally {
      setBusy(false);
    }
  };

  const renew = async (r: LicenseRow): Promise<void> => {
    setNotice(null);
    if (!renewEnd.trim()) { setNotice(t('m36.rights.validityEndReq')); return; }
    setBusyId(r.rightsLicenseId);
    try {
      await apiRequest(`/v1/dam/rights/${r.rightsLicenseId}/renew`, {
        method: 'POST',
        body: { validityEnd: renewEnd.trim() },
        token,
      });
      setNotice(t('m36.rights.renewed', { code: r.licenseCode }));
      setRenewId(null); setRenewEnd('');
      setReload((n) => n + 1);
    } catch (err) {
      on403(err, t('m36.rights.renewFailed'));
    } finally {
      setBusyId(null);
    }
  };

  const expireLapsed = async (): Promise<void> => {
    setNotice(null);
    setBusy(true);
    try {
      const r = await apiRequest<{ data: { expired: number } }>(`/v1/dam/rights/expire-lapsed`, { method: 'POST', token });
      setNotice(t('m36.rights.expireResult', { count: r.data.expired }));
      setReload((n) => n + 1);
    } catch (err) {
      on403(err, t('m36.rights.expireFailed'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <main aria-label="rights-management">
      <h1>{t('m36.rights.title')}</h1>
      <form aria-label="create-license-form" onSubmit={(e) => void create(e)}>
        <label>
          {t('m36.rights.nameLabel')}
          <input aria-label="field-name" value={newName} onChange={(e) => setNewName(e.target.value)} />
        </label>
        <label>
          {t('m36.common.type')}
          <select aria-label="field-type" value={newType} onChange={(e) => setNewType(e.target.value)}>
            {CREATE_TYPES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </label>
        <label>
          {t('m36.common.asset')}
          <input aria-label="field-asset" value={newAssetId} onChange={(e) => setNewAssetId(e.target.value)} placeholder={t('m36.common.assetIdPh')} />
        </label>
        <label>
          {t('m36.rights.owner')}
          <input aria-label="field-owner" value={newOwner} onChange={(e) => setNewOwner(e.target.value)} />
        </label>
        <label>
          {t('m36.rights.validityEndLabel')}
          <DateInput aria-label="field-validity-end" value={newValidityEnd} onValueChange={setNewValidityEnd} />
        </label>
        <Button variant="primary" type="submit" aria-label="create-license" disabled={busy}>{t('m36.rights.create')}</Button>
        <Button variant="secondary" type="button" aria-label="expire-lapsed" disabled={busy} onClick={() => void expireLapsed()}>{t('m36.rights.expireLapsed')}</Button>
      </form>
      {notice && <p role="status" aria-label="rights-notice">{notice}</p>}
      <div className="filters">
        <label>
          {t('m36.common.type')}
          <select aria-label="filter-type" value={licenseType} onChange={(e) => setLicenseType(e.target.value)}>
            {LICENSE_TYPES.map((s) => <option key={s || 'all'} value={s}>{s || t('m36.common.all')}</option>)}
          </select>
        </label>
        <label>
          {t('m36.common.status')}
          <select aria-label="filter-status" value={status} onChange={(e) => setStatus(e.target.value)}>
            {STATUSES.map((s) => <option key={s || 'all'} value={s}>{s || t('m36.common.all')}</option>)}
          </select>
        </label>
        <label>
          {t('common.search')}
          <input aria-label="filter-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('m36.rights.searchPh')} />
        </label>
      </div>
      {error && <p role="alert">{error}</p>}
      {loading ? <p>{t('common.loading')}</p> : (
        <>
          <p aria-label="license-count">{t('m36.rights.count', { count: total })}</p>
          <DataTable<LicenseRow>
            ariaLabel="license-table"
            testId="license-table"
            rowKey={(r) => r.rightsLicenseId}
            rowDataAttrs={(r) => ({ 'data-status': r.status, 'data-type': r.licenseType })}
            rows={rows}
            columns={[
              { key: 'code', header: t('m36.common.code'), render: (r) => r.licenseCode },
              { key: 'name', header: t('m36.common.name'), render: (r) => r.licenseName },
              { key: 'type', header: t('m36.common.type'), render: (r) => r.licenseType },
              { key: 'owner', header: t('m36.rights.owner'), render: (r) => r.copyrightOwner ?? '' },
              { key: 'asset', header: t('m36.common.asset'), render: (r) => r.assetId ?? '' },
              { key: 'status', header: t('m36.common.status'), render: (r) => r.status },
              { key: 'actions', header: t('m36.common.actions'), render: (r) => (
                renewId === r.rightsLicenseId ? (
                  <>
                    <DateInput
                      aria-label={`renew-end-${r.licenseCode}`}
                      value={renewEnd}
                      onValueChange={setRenewEnd}
                    />
                    <Button variant="secondary" type="button" aria-label={`confirm-renew-${r.licenseCode}`} disabled={busyId === r.rightsLicenseId || !renewEnd.trim()} onClick={() => void renew(r)}>{t('m36.rights.confirm')}</Button>
                    <Button variant="danger" type="button" aria-label={`cancel-renew-${r.licenseCode}`} onClick={() => { setRenewId(null); setRenewEnd(''); }}>{t('common.cancel')}</Button>
                  </>
                ) : (
                  <Button variant="secondary" type="button" aria-label={`renew-${r.licenseCode}`} onClick={() => { setRenewId(r.rightsLicenseId); setRenewEnd(''); setNotice(null); }}>{t('m36.rights.renew')}</Button>
                )
              ) },
            ]}
          />
        </>
      )}
    </main>
  );
}
